<template>
  <Teleport to="#modalHost">
    <Transition name="fade">
      <div
        v-if="isVisible"
        class="modal z-100 size-full font-pp-mori fixed bottom-0 left-0 top-0 flex max-h-screen w-screen justify-center overflow-x-hidden overscroll-none bg-white/70 backdrop-blur-sm p-4"
      >
        <div
          class="flex max-h-full w-full flex-col border border-[#D9D1CE] bg-white md:w-6/12"
          :data-test="`${name}--FrkModal`"
        >
          <div class="flex items-stretch rounded-none border-b border-[#D9D1CE] bg-white">
            <div class="flex flex-auto items-center gap-2 px-8 py-4">
              <slot name="head">
                <div class="font-regular text-xl text-[#281912]">
                  <slot name="title"></slot>
                </div>
                <FrkHelpIcon v-if="$slots['help-text']">
                  <template #help-text>
                    <slot name="help-text"> </slot>
                  </template>
                </FrkHelpIcon>
              </slot>
            </div>
            <button
              v-if="!hideCloseButton"
              @click="modal.hide()"
              data-test="CloseButton"
              class="ml-auto flex flex-none items-center justify-center border-l border-[#D9D1CE] bg-white px-6 text-[#281912]"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div
            class="max-h-full overflow-y-auto overscroll-none"
            :class="[
              'bg-white',
              'p-8',
              roundedBorderClass,
              startingFromBottom ? 'flex flex-col-reverse' : '',
            ]"
          >
            <div>
              <slot name="body"></slot>
            </div>
          </div>
          <div
            :class="['bg-white', 'px-8', 'py-4', 'text-left', 'rounded-none']"
            v-if="$slots['foot']"
          >
            <slot name="foot"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, useSlots } from 'vue';

import { useModal } from '@/composables';
import FrkHelpIcon from './FrkHelpIcon.vue';

const modal = useModal();
const slots = useSlots();

type Props = {
  name: string;
  hideCloseButton?: boolean;
  startingFromBottom?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  hideCloseButton: false,
  startingFromBottom: false,
});

const isVisible = computed(() => modal.currentModal === props.name);
const currentModal = computed(() => modal.currentModal);

const roundedBorderClass = computed(() => (slots.foot ? '' : 'rounded-none'));

const emit = defineEmits(['closed']);

watch(currentModal, async (value, oldValue) => {
  if (value === props.name) {
    show();
  } else if (oldValue === props.name) {
    emit('closed');
  }
});

function show() {
  window.addEventListener('keyup', ev => {
    if (ev.key === 'Escape' && isVisible.value) {
      modal.hide();
    }
  });
}
</script>
